import { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';

import { api, downloadFile, mutate } from '../shared/api';
import { formatDate, formatMoney, localIsoDate } from '../shared/formatting';
import { BrandSpinner } from '../shared/BrandSpinner';
import { Breadcrumb } from '../shared/Breadcrumb';

type Locale = 'hr' | 'en';

// Globalni `button` u app.css je velik (padding 13/16, font 900) pa retke tablice nabubri. Ovdje su gumbi kompaktni.
const BTN: CSSProperties = { padding: '4px 12px', fontSize: '0.8rem', fontWeight: 700, borderRadius: 6, lineHeight: 1.3 };
const BTN_QUIET: CSSProperties = { ...BTN, background: '#f4efe3', border: '1px solid #ddd5bd' };

/** Desni drawer za sve akcije modula (uredi, poslano, odbaci). Esc i klik na pozadinu zatvaraju. */
function Drawer({ title, onClose, error, children }: { title: string; onClose: () => void; error?: string | null; children: ReactNode }) {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);
    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000 }} onClick={onClose}>
            <div role="dialog" aria-label={title} onClick={(e) => e.stopPropagation()}
                style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 420, maxWidth: '94vw', background: '#fff', borderLeft: '1px solid #ddd5bd', boxShadow: '-6px 0 18px rgba(0,0,0,0.12)', padding: 18, overflowY: 'auto' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
                    <strong style={{ fontSize: '1rem' }}>{title}</strong>
                    <button type="button" onClick={onClose} aria-label="Zatvori" style={{ ...BTN_QUIET, marginLeft: 'auto', padding: '2px 9px' }}>✕</button>
                </div>
                {error ? <div style={{ borderLeft: '4px solid #b00020', background: '#fdf3f3', padding: '6px 10px', marginBottom: 12, fontSize: '0.85rem' }}>{error}</div> : null}
                {children}
            </div>
        </div>
    );
}

type Drawer = { kind: 'edit'; branchId: number } | { kind: 'sent'; p: ProposalRow } | { kind: 'discard'; p: ProposalRow };

type Proposal = { id: number; declared_value: number; increase_amount: number; reason: string; proposed_on: string };
type LastDecl = { id: number; declared_value: number; valid_from: string; valid_to: string; active: boolean };
type BranchRow = {
    branch_id: number;
    name: string;
    warehouse_codes: string[];
    base_amount: number | null;
    stock_value: number | null;
    cover_value: number | null;
    cover_source: 'declaration' | 'base' | null;
    exceeded: boolean;
    action: string | null;
    checked_at: string | null;
    last_declaration: LastDecl | null;
    proposal: Proposal | null;
};
type Overview = { date: string; rows: BranchRow[]; stock_basis: 'purchase' | 'retail'; validity_days: number };
type ProposalRow = { id: number; branch_id: number; name: string; reason: string; proposed_on: string; stock_value: number; previous_cover: number; declared_value: number; increase_amount: number };
type ReportRow = { branch_id: number; name: string; base_amount: number | null; declarations: number; total_increase: number; max_declared: number; first_from: string | null };
type Report = { year: number; rows: ReportRow[]; total: { declarations: number; total_increase: number }; first_declaration: string | null };
type HistoryRow = { id: number; name: string; reason: string; sent_on: string | null; valid_from: string; valid_to: string; stock_value: number; previous_cover: number; declared_value: number; increase_amount: number; source: string };

type Tab = 'today' | 'proposals' | 'report' | 'history';

const th: React.CSSProperties = { padding: 6, textAlign: 'left' };
const thr: React.CSSProperties = { padding: 6, textAlign: 'right' };
const tdr: React.CSSProperties = { padding: 6, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

export function InsuranceStockModule({ token, locale = 'hr', onBack }: { token: string; locale?: Locale; onBack?: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const money = (v: number | null | undefined) => (v === null || v === undefined ? '-' : formatMoney(v, locale));

    const [tab, setTab] = useState<Tab>('today');
    const [date, setDate] = useState(localIsoDate());
    const [overview, setOverview] = useState<Overview | null>(null);
    const [proposals, setProposals] = useState<ProposalRow[]>([]);
    const [report, setReport] = useState<Report | null>(null);
    const [history, setHistory] = useState<HistoryRow[]>([]);
    const [year, setYear] = useState(new Date().getFullYear());
    const [selected, setSelected] = useState<Set<number>>(new Set());
    const [histSelected, setHistSelected] = useState<Set<number>>(new Set());
    const [sentOn, setSentOn] = useState(localIsoDate());
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);

    // uređivanje početne svote i skladišta poslovnice
    const [drawer, setDrawer] = useState<Drawer | null>(null);
    const [editBase, setEditBase] = useState('');
    const [editCodes, setEditCodes] = useState('');

    const loadToday = useCallback(async () => {
        const [o, p] = await Promise.all([
            api<Overview>(`/insurance-stock/overview?date=${date}`, token),
            api<{ rows: ProposalRow[] }>('/insurance-stock/proposals', token),
        ]);
        setOverview(o);
        setProposals(p.rows);
    }, [date, token]);

    const reload = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            if (tab === 'report') {
                setReport(await api<Report>(`/insurance-stock/report?year=${year}`, token));
            } else if (tab === 'history') {
                setHistory((await api<{ rows: HistoryRow[] }>(`/insurance-stock/history?year=${year}`, token)).rows);
            } else {
                await loadToday();
            }
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setLoading(false);
        }
    }, [tab, year, token, loadToday]);

    useEffect(() => {
        void reload();
    }, [reload]);

    const run = async (fn: () => Promise<unknown>, okMsg?: string): Promise<boolean> => {
        setBusy(true);
        setError(null);
        setNotice(null);
        try {
            await fn();
            if (okMsg) setNotice(okMsg);
            await reload();
            return true;
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
            return false;
        } finally {
            setBusy(false);
        }
    };

    const openDrawer = (d: Drawer) => { setError(null); setDrawer(d); };
    const closeDrawer = useCallback(() => setDrawer(null), []);

    const checkNow = () =>
        run(async () => {
            const r = await mutate<{ new: number; updated: number }>('/insurance-stock/check', token, 'POST', { date });
            setNotice(t(`Provjera gotova: novih prijedloga ${r.new}, podignutih ${r.updated}.`, `Check done: ${r.new} new proposals, ${r.updated} raised.`));
        });

    const letter = () => {
        const ids = [...selected].join(',');
        if (!ids) return;
        void run(() => downloadFile(`/insurance-stock/letter?ids=${ids}&sent_on=${sentOn}`, token, `Povecanje-osiguranja-poslovnica-${sentOn}.docx`));
    };

    // Ponovni ispis dopisa za već poslane prijave; datum dopisa je datum slanja prijave.
    const historyLetter = () => {
        const ids = [...histSelected].join(',');
        if (!ids) return;
        const first = history.find((h) => histSelected.has(h.id));
        const stamp = (first?.sent_on ?? sentOn).slice(0, 10);
        void run(() => downloadFile(`/insurance-stock/letter?ids=${ids}`, token, `Povecanje-osiguranja-poslovnica-${stamp}.docx`));
    };

    const confirm = async (p: ProposalRow) => {
        const ok = await run(() => mutate(`/insurance-stock/proposals/${p.id}/confirm`, token, 'POST', { sent_on: sentOn }), t(`Prijava za ${p.name} označena kao poslana, rok teče od ${formatDate(sentOn, locale)}.`, `Declaration for ${p.name} marked as sent.`));
        if (ok) closeDrawer();
    };

    const cancel = async (p: ProposalRow) => {
        const ok = await run(() => mutate(`/insurance-stock/proposals/${p.id}/cancel`, token, 'POST'));
        if (ok) closeDrawer();
    };

    const saveEdit = async (branchId: number) => {
        const ok = await run(async () => {
            const base = Number(editBase.replace(/\./g, '').replace(',', '.'));
            await mutate(`/insurance-stock/branches/${branchId}`, token, 'PUT', {
                year: Number(date.slice(0, 4)),
                base_amount: base,
                warehouse_codes: editCodes.split(',').map((c) => c.trim()).filter(Boolean),
            });
        });
        if (ok) closeDrawer();
    };

    const totals = useMemo(() => {
        const rows = overview?.rows ?? [];
        return { exceeded: rows.filter((r) => r.exceeded).length, pending: rows.filter((r) => r.proposal).length, missing: rows.filter((r) => r.action === 'no_snapshot' || r.action === 'no_base').length };
    }, [overview]);

    const statusOf = (r: BranchRow): string => {
        if (r.action === 'no_base') return t('nema početne svote', 'no base amount');
        if (r.action === 'no_snapshot') return t('nema snimke zaliha', 'no stock snapshot');
        if (r.proposal) return t('prijedlog prijave', 'proposal');
        if (r.stock_value === null) return t('nije provjereno', 'not checked');
        return r.exceeded ? t('iznad pokrića', 'above cover') : t('u pokriću', 'covered');
    };

    const toggleHist = (id: number) => setHistSelected((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    const toggle = (id: number) => setSelected((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

    const tabBtn = (key: Tab, label: string) => (
        <button type="button" className={tab === key ? 'is-active' : ''} onClick={() => setTab(key)}>{label}</button>
    );

    return (
        <div className="module-shell">
            <Breadcrumb items={[{ label: '40', onClick: onBack }, { label: t('Osiguranje zaliha poslovnica', 'Branch stock insurance') }]} />
            <h2 style={{ marginBottom: 4 }}>{t('Osiguranje zaliha poslovnica', 'Branch stock insurance')} <span className="muted" style={{ fontSize: '0.9rem' }}>40.9</span></h2>
            <p className="muted" style={{ marginTop: 0, fontSize: '0.85rem' }}>
                {t(
                    `Zalihe poslovnice (${overview?.stock_basis === 'retail' ? 'maloprodajna' : 'nabavna'} vrijednost + roba na putu, sva skladišta) uspoređuju se dnevno sa važećom osiguranom svotom. Prijava vrijedi ${overview?.validity_days ?? 7} dana i obnavlja se pri svakom novom porastu. Pad zaliha ne traži ništa. Sustav ništa ne šalje osiguranju, dopis se ispisuje i potvrđuje ručno.`,
                    `Branch stock (${overview?.stock_basis === 'retail' ? 'retail' : 'purchase'} value + goods in transit, all warehouses) is compared daily with the insured sum. A declaration is valid for ${overview?.validity_days ?? 7} days and is renewed on every new increase. Nothing is sent to the insurer automatically.`,
                )}
            </p>

            <div className="bi-seg" role="tablist" style={{ marginBottom: 12 }}>
                {tabBtn('today', t('Danas', 'Today'))}
                {tabBtn('proposals', `${t('Prijedlozi', 'Proposals')} (${proposals.length})`)}
                {tabBtn('report', t('Podignuto u godini', 'Raised in year'))}
                {tabBtn('history', t('Povijest prijava', 'Declaration history'))}
            </div>

            {error ? <div className="card" style={{ borderLeft: '4px solid #b00020', marginBottom: 10 }}>{error}</div> : null}
            {notice ? <div className="card" style={{ borderLeft: '4px solid #2e7d32', marginBottom: 10 }}>{notice}</div> : null}
            {loading ? <BrandSpinner /> : null}

            {!loading && tab === 'today' && overview ? (
                <>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                        <label>{t('Datum', 'Date')} <input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
                        <button type="button" style={BTN} disabled={busy} onClick={() => void checkNow()}>{t('Provjeri sada', 'Check now')}</button>
                        <span className="muted" style={{ fontSize: '0.85rem' }}>
                            {t(`Iznad pokrića: ${totals.exceeded}, otvorenih prijedloga: ${totals.pending}, bez podataka: ${totals.missing}`, `Above cover: ${totals.exceeded}, open proposals: ${totals.pending}, missing data: ${totals.missing}`)}
                        </span>
                    </div>
                    <table style={{ width: '100%', fontSize: '0.82rem', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid #ccc' }}>
                                <th style={th}>{t('Poslovnica', 'Branch')}</th>
                                <th style={thr}>{t('Početno', 'Base')}</th>
                                <th style={thr}>{t('Zalihe', 'Stock')}</th>
                                <th style={thr}>{t('Važeće pokriće', 'Current cover')}</th>
                                <th style={th}>{t('Vrijedi do', 'Valid until')}</th>
                                <th style={thr}>{t('Razlika', 'Difference')}</th>
                                <th style={th}>{t('Status', 'Status')}</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {overview.rows.map((r) => (
                                <tr key={r.branch_id} style={{ borderBottom: '1px solid #eee', background: r.exceeded ? '#f2f2f2' : undefined }}>
                                    <td style={{ padding: 6 }}>{r.name}</td>
                                    <td style={tdr}>{money(r.base_amount)}</td>
                                    <td style={tdr}>{money(r.stock_value)}</td>
                                    <td style={tdr}>{money(r.cover_value)}</td>
                                    <td style={{ padding: 6 }}>{r.cover_source === 'declaration' && r.last_declaration ? formatDate(r.last_declaration.valid_to, locale) : r.cover_source === 'base' ? t('početna svota', 'base amount') : '-'}</td>
                                    <td style={tdr}>{r.stock_value !== null && r.cover_value !== null ? money(r.stock_value - r.cover_value) : '-'}</td>
                                    <td style={{ padding: 6, fontWeight: r.exceeded ? 700 : 400 }}>{statusOf(r)}</td>
                                    <td style={{ padding: 6 }}><button type="button" style={BTN_QUIET} onClick={() => { setEditBase(r.base_amount !== null ? String(r.base_amount).replace('.', ',') : ''); setEditCodes(r.warehouse_codes.join(', ')); openDrawer({ kind: 'edit', branchId: r.branch_id }); }}>{t('Uredi', 'Edit')}</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            ) : null}

            {!loading && tab === 'proposals' ? (
                <>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                        <label>{t('Datum slanja', 'Sending date')} <input type="date" value={sentOn} onChange={(e) => setSentOn(e.target.value)} /></label>
                        <button type="button" style={BTN} disabled={busy || selected.size === 0} onClick={letter}>{t('Ispiši dopis (DOCX)', 'Letter (DOCX)')}</button>
                        <span className="muted" style={{ fontSize: '0.85rem' }}>{t('Nakon slanja označi svaku poslovnicu kao poslanu, tek tada teče rok od 7 dana.', 'After sending, mark each branch as sent; the 7-day period starts then.')}</span>
                    </div>
                    {proposals.length === 0 ? <p className="muted">{t('Nema otvorenih prijedloga.', 'No open proposals.')}</p> : (
                        <table style={{ width: '100%', fontSize: '0.82rem', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid #ccc' }}>
                                    <th style={th} />
                                    <th style={th}>{t('Poslovnica', 'Branch')}</th>
                                    <th style={th}>{t('Razlog', 'Reason')}</th>
                                    <th style={thr}>{t('Pokriće do sada', 'Cover so far')}</th>
                                    <th style={thr}>{t('Zalihe', 'Stock')}</th>
                                    <th style={thr}>{t('Povećati za', 'Raise by')}</th>
                                    <th style={thr}>{t('Nova svota', 'New sum')}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {proposals.map((p) => (
                                    <tr key={p.id} style={{ borderBottom: '1px solid #eee' }}>
                                        <td style={{ padding: 6 }}><input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} /></td>
                                        <td style={{ padding: 6 }}>{p.name}</td>
                                        <td style={{ padding: 6 }}>{p.reason === 'expiry' ? t('istek prijave', 'expiry') : t('porast zaliha', 'stock increase')} · {formatDate(p.proposed_on, locale)}</td>
                                        <td style={tdr}>{money(p.previous_cover)}</td>
                                        <td style={tdr}>{money(p.stock_value)}</td>
                                        <td style={tdr}>{money(p.increase_amount)}</td>
                                        <td style={tdr}>{money(p.declared_value)}</td>
                                        <td style={{ padding: 6, whiteSpace: 'nowrap' }}>
                                            <button type="button" style={BTN_QUIET} disabled={busy} onClick={() => openDrawer({ kind: 'sent', p })}>{t('Poslano', 'Sent')}</button>{' '}
                                            <button type="button" style={BTN_QUIET} disabled={busy} onClick={() => openDrawer({ kind: 'discard', p })}>{t('Odbaci', 'Discard')}</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </>
            ) : null}

            {!loading && tab === 'report' && report ? (
                <>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }} className="no-print">
                        <label>{t('Godina', 'Year')} <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ width: 90 }} /></label>
                        <button type="button" style={BTN_QUIET} onClick={() => window.print()}>{t('Ispis', 'Print')}</button>
                    </div>
                    <p className="muted" style={{ fontSize: '0.85rem' }}>
                        {t('Podignuto osiguranje je zbroj povećanja poslanih prijava u godini, svaka prijava računa razliku prema pokriću koje je vrijedilo prije nje.', 'Raised insurance is the sum of increases of all sent declarations in the year.')}
                        {report.first_declaration ? ` ${t('Prva evidentirana prijava', 'First recorded declaration')}: ${formatDate(report.first_declaration, locale)}.` : ''}
                    </p>
                    <table style={{ width: '100%', fontSize: '0.82rem', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid #ccc' }}>
                                <th style={th}>{t('Poslovnica', 'Branch')}</th>
                                <th style={thr}>{t('Početno', 'Base')}</th>
                                <th style={thr}>{t('Broj prijava', 'Declarations')}</th>
                                <th style={thr}>{t('Najviša prijavljena svota', 'Highest declared sum')}</th>
                                <th style={thr}>{t('Ukupno podignuto', 'Total raised')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {report.rows.map((r) => (
                                <tr key={r.branch_id} style={{ borderBottom: '1px solid #eee' }}>
                                    <td style={{ padding: 6 }}>{r.name}</td>
                                    <td style={tdr}>{money(r.base_amount)}</td>
                                    <td style={tdr}>{r.declarations}</td>
                                    <td style={tdr}>{r.declarations ? money(r.max_declared) : '-'}</td>
                                    <td style={tdr}>{money(r.total_increase)}</td>
                                </tr>
                            ))}
                            <tr style={{ borderTop: '2px solid #333', fontWeight: 700 }}>
                                <td style={{ padding: 6 }}>{t('Ukupno', 'Total')}</td>
                                <td />
                                <td style={tdr}>{report.total.declarations}</td>
                                <td />
                                <td style={tdr}>{money(report.total.total_increase)}</td>
                            </tr>
                        </tbody>
                    </table>
                </>
            ) : null}

            {!loading && tab === 'history' ? (
                <>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                        <label>{t('Godina', 'Year')} <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ width: 90 }} /></label>
                        <button type="button" style={BTN} disabled={busy || histSelected.size === 0} onClick={historyLetter}>{t('Ispiši dopis (DOCX)', 'Letter (DOCX)')}</button>
                        <span className="muted" style={{ fontSize: '0.85rem' }}>{t('Označi poslane prijave za ponovni ispis dopisa. Datum dopisa je datum slanja prijave.', 'Select sent declarations to print the letter again. The letter is dated with the sending date.')}</span>
                    </div>
                    <table style={{ width: '100%', fontSize: '0.82rem', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid #ccc' }}>
                                <th style={th} />
                                <th style={th}>{t('Poslovnica', 'Branch')}</th>
                                <th style={th}>{t('Vrijedi', 'Valid')}</th>
                                <th style={thr}>{t('Pokriće prije', 'Cover before')}</th>
                                <th style={thr}>{t('Zalihe', 'Stock')}</th>
                                <th style={thr}>{t('Prijavljeno', 'Declared')}</th>
                                <th style={thr}>{t('Podignuto', 'Raised')}</th>
                                <th style={th}>{t('Izvor', 'Source')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.map((h) => (
                                <tr key={h.id} style={{ borderBottom: '1px solid #eee' }}>
                                    <td style={{ padding: 6 }}><input type="checkbox" disabled={h.source === 'excel'} title={h.source === 'excel' ? t('Uvezeno iz Excela, nema podataka za dopis', 'Imported from Excel, no letter data') : undefined} checked={histSelected.has(h.id)} onChange={() => toggleHist(h.id)} /></td>
                                    <td style={{ padding: 6 }}>{h.name}</td>
                                    <td style={{ padding: 6 }}>{formatDate(h.valid_from, locale)} - {formatDate(h.valid_to, locale)}</td>
                                    <td style={tdr}>{money(h.previous_cover)}</td>
                                    <td style={tdr}>{money(h.stock_value)}</td>
                                    <td style={tdr}>{money(h.declared_value)}</td>
                                    <td style={tdr}>{money(h.increase_amount)}</td>
                                    <td style={{ padding: 6 }}>{h.source === 'excel' ? 'Excel' : t('modul', 'module')}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            ) : null}

            {drawer?.kind === 'edit' ? (() => {
                const row = overview?.rows.find((r) => r.branch_id === drawer.branchId);
                return (
                    <Drawer title={t('Uredi poslovnicu', 'Edit branch')} onClose={closeDrawer} error={error}>
                        <div style={{ fontWeight: 700, marginBottom: 12 }}>{row?.name}</div>
                        <label style={{ display: 'block', marginBottom: 12 }}>{t(`Početna svota ${date.slice(0, 4)}`, `Base amount ${date.slice(0, 4)}`)}<br />
                            <input value={editBase} onChange={(e) => setEditBase(e.target.value)} style={{ width: '100%' }} />
                        </label>
                        <label style={{ display: 'block', marginBottom: 16 }}>{t('Šifre skladišta (zarezom)', 'Warehouse codes (comma)')}<br />
                            <input value={editCodes} onChange={(e) => setEditCodes(e.target.value)} style={{ width: '100%' }} />
                        </label>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button type="button" style={BTN} disabled={busy} onClick={() => void saveEdit(drawer.branchId)}>{t('Spremi', 'Save')}</button>
                            <button type="button" style={BTN_QUIET} onClick={closeDrawer}>{t('Odustani', 'Cancel')}</button>
                        </div>
                    </Drawer>
                );
            })() : null}

            {drawer && drawer.kind !== 'edit' ? (
                <Drawer title={drawer.kind === 'sent' ? t('Prijava poslana', 'Declaration sent') : t('Odbaci prijedlog', 'Discard proposal')} onClose={closeDrawer} error={error}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{drawer.p.name}</div>
                    <div className="muted" style={{ fontSize: '0.85rem', marginBottom: 12 }}>
                        {drawer.p.reason === 'expiry' ? t('istek prijave', 'expiry') : t('porast zaliha', 'stock increase')} · {formatDate(drawer.p.proposed_on, locale)}
                    </div>
                    {([
                        [t('Pokriće do sada', 'Cover so far'), money(drawer.p.previous_cover)],
                        [t('Zalihe', 'Stock'), money(drawer.p.stock_value)],
                        [t('Povećati za', 'Raise by'), money(drawer.p.increase_amount)],
                        [t('Nova svota', 'New sum'), money(drawer.p.declared_value)],
                    ] as [string, string][]).map(([k, v]) => (
                        <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderTop: '1px solid #eee', fontSize: '0.85rem' }}>
                            <span className="muted">{k}</span><span style={{ fontVariantNumeric: 'tabular-nums' }}>{v}</span>
                        </div>
                    ))}
                    {drawer.kind === 'sent' ? (
                        <>
                            <label style={{ display: 'block', margin: '14px 0' }}>{t('Datum slanja', 'Sending date')}<br />
                                <input type="date" value={sentOn} onChange={(e) => setSentOn(e.target.value)} />
                            </label>
                            <p className="muted" style={{ fontSize: '0.8rem', marginTop: 0 }}>{t('Od ovog datuma teče rok od 7 dana.', 'The 7-day period starts on this date.')}</p>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button type="button" style={BTN} disabled={busy} onClick={() => void confirm(drawer.p)}>{t('Potvrdi poslano', 'Confirm sent')}</button>
                                <button type="button" style={BTN_QUIET} onClick={closeDrawer}>{t('Odustani', 'Cancel')}</button>
                            </div>
                        </>
                    ) : (
                        <>
                            <p style={{ margin: '14px 0', fontSize: '0.85rem' }}>{t(`Odbaciti prijedlog za ${drawer.p.name}?`, `Discard proposal for ${drawer.p.name}?`)}</p>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button type="button" style={BTN} disabled={busy} onClick={() => void cancel(drawer.p)}>{t('Odbaci prijedlog', 'Discard proposal')}</button>
                                <button type="button" style={BTN_QUIET} onClick={closeDrawer}>{t('Odustani', 'Cancel')}</button>
                            </div>
                        </>
                    )}
                </Drawer>
            ) : null}
        </div>
    );
}
